import React, {
  forwardRef,
  useEffect,
  useRef,
  useCallback,
  useState,
} from 'react';
import {Typeahead} from 'react-bootstrap-typeahead';
import generateRandomID from 'web/utils/generateRandomID';
import moment from 'moment-timezone';
import {Modal as BSModal} from 'bootstrap';
import {BaseComponent} from 'web/components/BaseComponent';
import enumList from 'web/utils/enumList';
import useDebounce from 'web/modules/dispatch-calendar/hooks/useDebounce';
import {LIMIT_PER_PAGE} from 'web/constant';

const {moduleEnums, accessModeEnums} = enumList;

/**
 * @typedef {object} TypeaheadMachineData
 * @property {number} id
 * @property {string} label
 */

/**
 * @typedef {object} CustomerMachineAssignmentData
 * @property {string} customerCode
 * @property {string} customerName
 * @property {TypeaheadMachineData[]} machines
 * @property {number} deliveryDate
 * @property {string} flgIsResold
 * @property {string} flgOnSiteCheck
 */

/**
 * @typedef {object} BaseMachineAssignmentManageModalProps
 * @property {object} title
 * @property {string} title.content
 * @property {React.Component} title.badge
 * @property {CustomerMachineAssignmentData} data
 * @property {
 *   React.Dispatch<React.SetStateAction<CustomerMachineAssignmentData>>
 * } setData
 * @property {function} handleSubmit
 * @property {object[]} machines
 * @property {string} type
 * @property {React.MutableRefObject<BSModal|undefined>} currentModalRef
 * @property {React.MutableRefObject<BSModal|undefined>} listModalRef
 */

/**
 * @return {CustomerMachineAssignmentData}
 */
export const getEmptyData = () =>
  JSON.parse(
    JSON.stringify({
      customerCode: '',
      customerName: '',
      machines: [],
      deliveryDate: '',
      flgIsResold: 'N',
      flgOnSiteCheck: 'N',
    }),
  );

/**
 * @param {BaseMachineAssignmentManageModalProps} props
 * @param {React.Ref} ref
 * @return {React.Component}
 */
const BaseMachineAssignmentManageModal = (props, ref) => {
  const baseComponent = new BaseComponent(props);

  const {
    title,
    data,
    setData,
    handleSubmit,
    machines,
    type,
    isValid,
    currentModalRef,
    isLoading,
    setIsLoading,
    listModalRef,
    updateMachineList,
    displayMachines,
    setDisplayMachines,
  } = props;

  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [searchPage, setSearchPage] = useState(1);
  const MAX_RESULT = LIMIT_PER_PAGE - 1;

  /** @type {React.MutableRefObject<Map<string, string>>} */
  const formHTMLIDRef = useRef(new Map());
  const getElementHTMLID = useCallback((elementName) => {
    if (!formHTMLIDRef.current.has(elementName)) {
      formHTMLIDRef.current.set(elementName, generateRandomID(20));
    }
    return formHTMLIDRef.current.get(elementName);
  });

  const baseHandleSubmit = async (e) => {
    try {
      await handleSubmit(e);
    } finally {
      if (ref.current instanceof HTMLDivElement) {
        currentModalRef.current.hide();
      }
    }
  };

  useEffect(() => {
    const handleModalHidden = () => {
      // setData(getEmptyData());
      dispose();
    };
    if (!(ref.current instanceof HTMLDivElement)) {
      return;
    }
    currentModalRef.current = new BSModal(ref.current);
    ref.current.addEventListener('hidden.bs.modal', handleModalHidden);
    return () => {
      if (!(ref.current instanceof HTMLDivElement)) {
        return;
      }
      currentModalRef.current = undefined;
      ref.current.removeEventListener('hidden.bs.modal', handleModalHidden);
      formHTMLIDRef.current.clear();
    };
  }, []);

  const dispose = () => {
    listModalRef.current.show();
    setPage(1);
    setSearchPage(1);
    setSearchText('');
  };

  const handleMenuPaginate = async () => {
    const nextPage = searchText.length ? searchPage + 1 : page + 1;

    const query = {
      page: nextPage,
      machineSerialNumber: searchText,
      isModifyState: true,
    };

    if (searchText.length) {
      query.isModifyState = false;
      setSearchPage(nextPage);
    } else {
      setPage(nextPage);
    }

    const result = await updateMachineList(query);
    setDisplayMachines((prev) => [...prev, ...result]);
  };

  const handleMenuSearch = async (text) => {
    try {
      if (!text.length) {
        setDisplayMachines(machines);
        return setSearchText('');
      }
      const result = await updateMachineList({
        machineSerialNumber: text,
        isModifyState: false,
      });
      setSearchText(text);
      setDisplayMachines(result);
    } finally {
      setSearchPage(1);
      setIsLoading(false);
    }
  };
  const onSearch = useDebounce(handleMenuSearch, 1000);

  const shouldLoadMore = (remainingCount) => remainingCount <= MAX_RESULT;
  return (
    <div
      ref={ref}
      className="modal fade"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <form
          className="modal__form modal-content needs-validation"
          noValidate
          onSubmit={baseHandleSubmit}
        >
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="modalCustomerLabel">
              {title.badge && (
                <div className="modal__title__badge">{title.badge}</div>
              )}
              <div className="modal__title__text">{title.content}</div>
            </h1>
            <button
              type="button"
              className="btn-close btn-cancel"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <div className="d-flex flex-column gap-3 px-2">
              <div className="modal__form__catagory row">
                <div className="col-6">
                  <label
                    htmlFor={getElementHTMLID('customerCode')}
                    className="form-label"
                  >
                    客戶代號
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    id={getElementHTMLID('customerCode')}
                    value={data.customerCode}
                    disabled
                  />
                </div>
                <div className="col-6">
                  <label
                    htmlFor={getElementHTMLID('customerName')}
                    className="form-label"
                  >
                    客戶名稱
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    id={getElementHTMLID('customerName')}
                    value={data.customerName}
                    disabled
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="customerName" className="form-label">
                    綁定的機器
                  </label>
                  <Typeahead
                    disabled={
                      !(
                        type === 'create' &&
                        baseComponent.checkPermission(
                          moduleEnums.customer,
                          accessModeEnums.update,
                        )
                      )
                    }
                    paginate
                    onPaginate={handleMenuPaginate}
                    maxResults={MAX_RESULT}
                    emptyLabel={isLoading ? '載入中...' : '查無資料'}
                    onInputChange={(text) => {
                      setIsLoading(true);
                      onSearch(text);
                    }}
                    required={type === 'create'}
                    id={getElementHTMLID('machineData')}
                    onChange={(machines) => {
                      setData({...data, machines});
                      const remainingCount =
                        displayMachines.length - (data.machines.length + 1);
                      if (shouldLoadMore(remainingCount)) handleMenuPaginate();
                    }}
                    selected={data.machines}
                    options={displayMachines}
                    paginationText="載入更多"
                    isLoading={isLoading}
                    size="sm"
                    placeholder="請選擇機器"
                    multiple
                    labelKey="label"
                  />
                </div>
                <div className="col-6">
                  <label
                    htmlFor={getElementHTMLID('deliveryDate')}
                    className="form-label"
                  >
                    保固起始日
                  </label>
                  <input
                    type="date"
                    id={getElementHTMLID('deliveryDate')}
                    className="form-control form-control-sm"
                    value={moment
                      .tz(data.deliveryDate, 'UTC')
                      .format('YYYY-MM-DD')}
                    onChange={(e) =>
                      setData({
                        ...data,
                        deliveryDate: moment
                          .tz(e.target.value, 'YYYY-MM-DD', 'UTC')
                          .valueOf(),
                      })
                    }
                    disabled={
                      !baseComponent.checkPermission(
                        moduleEnums.customer,
                        accessModeEnums.update,
                      )
                    }
                  />
                </div>
                <div className="col-6">
                  <label
                    htmlFor={getElementHTMLID('flgIsResold')}
                    className="form-label"
                  >
                    是否轉賣
                  </label>
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={data.flgIsResold === 'Y'}
                      id={getElementHTMLID('flgIsResold')}
                      role="switch"
                      disabled={
                        !baseComponent.checkPermission(
                          moduleEnums.customer,
                          accessModeEnums.update,
                        )
                      }
                      onChange={(event) =>
                        setData({
                          ...data,
                          flgIsResold: event.target.checked ? 'Y' : 'N',
                        })
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor={getElementHTMLID('flgIsResold')}
                    >
                      {data.flgIsResold === 'N' && <>非</>}轉賣
                    </label>
                  </div>
                </div>
                <div className="col-6">
                  <label
                    htmlFor={getElementHTMLID('flgOnSiteCheck')}
                    className="form-label"
                  >
                    需外勤
                  </label>
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={data.flgOnSiteCheck === 'Y'}
                      id={getElementHTMLID('flgOnSiteCheck')}
                      role="switch"
                      disabled={
                        !baseComponent.checkPermission(
                          moduleEnums.customer,
                          accessModeEnums.update,
                        )
                      }
                      onChange={(event) =>
                        setData({
                          ...data,
                          flgOnSiteCheck: event.target.checked ? 'Y' : 'N',
                        })
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor={getElementHTMLID('flgOnSiteCheck')}
                    >
                      {data.flgOnSiteCheck === 'N' && <>不</>}需外勤
                    </label>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <div
                    className="alert alert-warning global-invalid-feedback"
                    style={{display: isValid ? 'none' : 'block'}}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-cancel"
              data-bs-dismiss="modal"
            >
              取消
            </button>
            <button type="submit" className="btn btn-primary">
              儲存
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default forwardRef(BaseMachineAssignmentManageModal);
